import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../../support/AsyncStorageProvider';

interface IAnonymousViewerSessionStorage{
    // the session token used to get an access token, for access to a single caremap
    sessions: string[];
    sessionToView?: string;
}

type ISessionToken = string;

export class AnonymousViewerSessionManager {
    static inject = () => [
        AsyncStorageProvider,
    ];
    constructor(
        storage: AsyncStorageProvider,
    ){
        this.storage = storage.getJSONProvider<IAnonymousViewerSessionStorage>('AnonymousViewerSessionManager', { envNeutral: false, userNeutral: true });
    }

    private storage: IAsyncSmartStorageProvider<IAnonymousViewerSessionStorage>;
    private sessions: IAnonymousViewerSessionStorage['sessions'] = [];
    private sessionToView?: string;

    init = async () => {
        const fromStorage = await this.storage.get() || { sessions: [] };
        this.sessions = fromStorage.sessions;
        this.sessionToView = fromStorage.sessionToView;
        const newAnonSession = this.maybeGetAnonymousSessionFromUrl();
        if(newAnonSession){
            await this.addSession(newAnonSession);

            await this.reloadWithoutQueryParam();
        }
    };

    hasSessions = () => this.getSessions().length > 0;

    getSessions = () => this.sessions;

    getMostRecentSession = (): string | undefined => this.sessions[this.sessions.length - 1];

    private reloadWithoutQueryParam = async () => {
        const query = new URLSearchParams(window.location.search);
        query.delete('v');
        const queryString = query.toString();
        const finalQueryString = queryString ? `?${queryString}` : '';
        window.location.href = `${window.location.pathname}${finalQueryString}${window.location.hash}`;
        // TODO: never
        await new Promise(resolve => {

        });
    };

    private maybeGetAnonymousSessionFromUrl = (): string => new URLSearchParams(window.location.search).get('v') || '';

    private addSession = async (session: string) => {
        if (!this.sessions.includes(session)){
            this.sessions.push(session);
            await this.storage.set({ sessions: this.sessions, sessionToView: session });
        }
    };

    removeSession = async (session_token: string) => {
        if (this.sessionToView === session_token){
            this.sessionToView = undefined;
        }

        if (this.sessions.includes(session_token)){
            this.sessions = this.sessions.filter(s => s !== session_token);
            await this.storage.set({ sessions: this.sessions });
        }

    };

    getAndExpireSessionToView = (): string | undefined => {
        if (!this.sessionToView){
            return undefined;
        }

        const sessionToView = this.sessionToView;

        this.sessionToView = undefined;
        // intentionally not waiting
        this.storage.set({ sessionToView: undefined, sessions: this.sessions });

        return sessionToView;
    };

    /**
     * newer means - expires farther into the future
     * older means - expires sooner
     * but where this is used, that would likely do more harm than good to capture in the names
     */
    replaceSessionForSameCaremap = ({ newer, older }: { newer: ISessionToken; older: ISessionToken; }) => {
        // if we're about to remove older, but it was how we knew to redirect
        // keep our signal that we need to redirect
        // IOW - if they come in with v=caremap1-but-old-token
        //  we still want to go to caremap1 (just using our newer token, not the token they provided)
        if (this.sessionToView === older){
            this.sessionToView = newer;
        }

        // remove older, since we only want the one that will expire the farthest in the future
        this.removeSession(older);
    };
}
